import type { Board, BoardRegion } from '../protocol/ui';
import type { ObjectView } from '../model/table';
import type { BoardVisual } from './types';
import { edgeGeometry } from './edgeGeometry';

export interface Viewport { x: number; y: number; w: number; h: number }
/** Surfaces represent geometry; tokens/markers sharing their ref remain separate occupants. */
export function boardRepresentatives(b: Board, objects: ObjectView[]): Map<string, ObjectView> {
  const refs = new Map<string, ObjectView>();
  const regions = new Set(b.regions.map(r => r.id)), nodes = new Set(b.nodes.map(n => n.id)), edges = new Set(b.edges.map(e => e.id));
  for (const o of objects) {
    if (!o.ref) continue;
    const matches = regions.has(o.ref) ? o.kind === 'tile' || o.kind === 'space' : nodes.has(o.ref) ? o.kind === 'node' : edges.has(o.ref) && o.kind === 'edge';
    if (matches && (!refs.has(o.ref) || (o.kind === 'tile' && refs.get(o.ref)!.kind === 'space'))) refs.set(o.ref, o);
  }
  return refs;
}
export function regionBounds(r: BoardRegion): Viewport {
  const xs = r.points.map(p => p[0]), ys = r.points.map(p => p[1]);
  const x = Math.min(...xs), y = Math.min(...ys);
  return { x, y, w: Math.max(...xs) - x, h: Math.max(...ys) - y };
}
export function clampView(v: Viewport, width: number, height: number): Viewport {
  const w = Math.min(width, Math.max(width / 8, v.w)), h = w * height / width;
  return { x: Math.max(0, Math.min(width - w, v.x)), y: Math.max(0, Math.min(height - h, v.y)), w, h };
}
/** Optional public presentation metadata; malformed sizes keep the brick's default. */
export function boardPieceSize(o: ObjectView, field: unknown, fallback: number): number {
  const size = typeof field === 'string' ? o.fields[field] : undefined;
  return typeof size === 'number' && Number.isFinite(size) && size >= 8 && size <= 200 ? size : fallback;
}
export function objectPosition(o: ObjectView, b: Board, objects: Map<string, ObjectView>, seen = new Set<string>(), visual?: BoardVisual): [number, number] | null {
  if (seen.has(o.id) || seen.size >= 16) return null; seen.add(o.id);
  if (o.stack) { const parent = objects.get(o.stack); const p = parent ? objectPosition(parent, b, objects, seen, visual) : null; if (p) return [p[0] + b.width / 80, p[1] + b.height / 80]; }
  // Explicit, finite, observed occupant coordinates can offset a marker/token
  // without changing the topology or the terrain object's target.
  const x = o.fields?.x, y = o.fields?.y;
  if ((o.kind === 'marker' || o.kind === 'token') && typeof x === 'number' && typeof y === 'number' && Number.isFinite(x) && Number.isFinite(y) && x >= 0 && y >= 0 && x <= b.width && y <= b.height) return [x, y];
  const id = o.at ?? o.ref;
  const n = b.nodes.find(n => n.id === id); if (n) return [n.x, n.y];
  const r = b.regions.find(r => r.id === id);
  if (r) return [r.points.reduce((s, p) => s + p[0], 0) / r.points.length, r.points.reduce((s, p) => s + p[1], 0) / r.points.length];
  const e = b.edges.find(e => e.id === id), a = e && b.nodes.find(n => n.id === e.a), z = e && b.nodes.find(n => n.id === e.b);
  return a && z ? edgeGeometry(b, e!, visual).midpoint : null;
}

/** A presentation-only preview of an already declared complete command. Values name
 * observed objects through a scalar field; missing or ambiguous positions draw no path.
 * No adjacency, captures, legality or hidden state is evaluated here. */
export function choicePath(data: Record<string, unknown>, b: Board, objects: ObjectView[], field: string, key: string): [number, number][] {
  const values = data[field];
  if (!Array.isArray(values) || values.length < 2 || values.length > 64) return [];
  const byId = new Map(objects.map(o => [o.id, o]));
  const out: [number, number][] = [];
  for (const value of values) {
    if (typeof value !== 'string' && typeof value !== 'number') return [];
    const positions = objects.filter(o => o.fields[key] === value).map(o => objectPosition(o, b, byId)).filter((p): p is [number, number] => p !== null);
    if (!positions.length || positions.some(p => p[0] !== positions[0]![0] || p[1] !== positions[0]![1])) return [];
    out.push(positions[0]!);
  }
  return out;
}
